import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { ArrowRight } from '@phosphor-icons/react';
import noImage from '../assets/no-image.png';
import { colors, typography } from '../theme/tokens';
import { supabase } from '../config/supabase';

interface MealItemProps {
  meal_type: string;
  recipe_id?: number | null;
}

const Container = styled.div`
  display: flex;
  align-items: center;
  background: ${colors.neutral.white};
  border-radius: 8px;
  gap: 1rem;
`;

const Thumbnail = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 8px;
  overflow: hidden;
  background: ${colors.neutral.lightCharcoal};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Img = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const TextContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-width: 0;
`;

const MealType = styled.div`
  font-size: 0.85rem;
  color: ${colors.neutral.charcoal};
  opacity: 0.7;
  font-family: ${typography.label.medium.fontFamily};
  line-height: 1.2;
`;

const RecipeTitle = styled.div`
  font-size: 1rem;
  color: ${colors.neutral.darkCharcoal};
  font-family: ${typography.paragraph.medium.fontFamily};
  font-weight: 500;
  line-height: 1.3;
  /* Allow wrapping, remove truncation */
`;

const SkeletonBox = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 8px;
  background: ${colors.neutral.lightCharcoal};
  animation: pulse 1.2s infinite ease-in-out;
  @keyframes pulse {
    0% { opacity: 1; }
    50% { opacity: 0.5; }
    100% { opacity: 1; }
  }
`;

const SkeletonText = styled.div`
  height: 16px;
  width: 80%;
  background: ${colors.neutral.lightCharcoal};
  border-radius: 4px;
  margin: 4px 0;
  animation: pulse 1.2s infinite ease-in-out;
`;

const ArrowContainer = styled.div`
  margin-left: 1rem;
  display: flex;
  align-items: center;
`;

const MealItem: React.FC<MealItemProps> = ({ meal_type, recipe_id }) => {
  const [recipe, setRecipe] = useState<{ Name: string; ImageURL: string } | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let ignore = false;
    const fetchRecipe = async () => {
      if (recipe_id) {
        setLoading(true);
        const { data } = await supabase
          .from('Recipes')
          .select('Name, ImageURL')
          .eq('RecipeId', recipe_id)
          .single();
        if (!ignore) setRecipe(data);
        setLoading(false);
      } else {
        setRecipe(null);
      }
    };
    fetchRecipe();
    return () => { ignore = true; };
  }, [recipe_id]);

  return (
    <Container>
      {loading ? (
        <SkeletonBox />
      ) : (
        <Thumbnail>
          <Img src={recipe?.ImageURL || noImage} alt={recipe?.Name || 'No image'} />
        </Thumbnail>
      )}
      <TextContainer>
        <MealType>{meal_type}</MealType>
        {loading ? (
          <SkeletonText style={{ width: '60%' }} />
        ) : (
          <RecipeTitle>{recipe ? recipe.Name : 'pick a recipe'}</RecipeTitle>
        )}
      </TextContainer>
      <ArrowContainer>
        <ArrowRight size={24} color={colors.neutral.charcoal} />
      </ArrowContainer>
    </Container>
  );
};

export default MealItem; 